import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface BomRevisionSummary {
  id: number;
  bom_id: number;
  revision_label: string;
  frozen_at: string | null;
  frozen_by_user_id: number | null;
  line_count: number;
}

export interface Bom {
  id: number;
  name: string;
  description: string | null;
  revision_count: number;
  latest_revision: BomRevisionSummary | null;
  created_at: string;
  updated_at: string;
  row_version: number;
}

export interface BomLine {
  id: number;
  part_id: number;
  part_number: string;
  mfg_pn: string | null;
  description: string | null;
  reference_designators: string[] | null;
  quantity: number;
  notes: string | null;
}

export interface BomRevisionDetail extends BomRevisionSummary {
  lines: BomLine[];
}

export interface AvailabilityLine {
  line_id: number;
  part_id: number;
  part_number: string;
  mfg_pn: string | null;
  description: string | null;
  reference_designators: string[] | null;
  quantity_per_board: number;
  quantity_needed: number;
  quantity_on_hand: number;
  quantity_is_approximate: boolean;
  shortfall: number;
}

export interface AvailabilityResult {
  bom_id: number;
  revision_id: number;
  revision_label: string;
  revision_frozen: boolean;
  build_qty: number;
  lines: AvailabilityLine[];
  can_build: boolean;
  shortage_count: number;
}

export interface WhereUsedRow {
  bom_id: number;
  bom_name: string;
  revision_id: number;
  revision_label: string;
  revision_frozen: boolean;
  quantity_per_board: number;
}

const bomsKey = (slug: string | undefined) => ['boms', slug] as const;
const bomKey = (slug: string | undefined, id: number | null) => ['bom', slug, id] as const;
const revisionsKey = (slug: string | undefined, id: number | null) =>
  ['bom-revisions', slug, id] as const;
const revisionKey = (slug: string | undefined, bid: number, rid: number | null) =>
  ['bom-revision', slug, bid, rid] as const;

export function useBoms(orgSlug: string | undefined) {
  return useQuery<Bom[]>({
    queryKey: bomsKey(orgSlug),
    queryFn: () => api.get<Bom[]>(`/api/orgs/${orgSlug}/boms`),
    enabled: Boolean(orgSlug),
  });
}

export function useBom(orgSlug: string | undefined, bomId: number | null) {
  return useQuery<Bom>({
    queryKey: bomKey(orgSlug, bomId),
    queryFn: () => api.get<Bom>(`/api/orgs/${orgSlug}/boms/${bomId}`),
    enabled: Boolean(orgSlug && bomId),
  });
}

export function useRevisions(orgSlug: string | undefined, bomId: number | null) {
  return useQuery<BomRevisionSummary[]>({
    queryKey: revisionsKey(orgSlug, bomId),
    queryFn: () =>
      api.get<BomRevisionSummary[]>(`/api/orgs/${orgSlug}/boms/${bomId}/revisions`),
    enabled: Boolean(orgSlug && bomId),
  });
}

export function useRevisionDetail(
  orgSlug: string | undefined,
  bomId: number | null,
  revisionId: number | null,
) {
  return useQuery<BomRevisionDetail>({
    queryKey: revisionKey(orgSlug, bomId ?? -1, revisionId),
    queryFn: () =>
      api.get<BomRevisionDetail>(
        `/api/orgs/${orgSlug}/boms/${bomId}/revisions/${revisionId}`,
      ),
    enabled: Boolean(orgSlug && bomId && revisionId),
  });
}

export function useCreateBom(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string | null }) =>
      api.postJson<Bom>(`/api/orgs/${orgSlug}/boms`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: bomsKey(orgSlug) }),
  });
}

export function useDeleteBom(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bomId: number) =>
      api.delete<unknown>(`/api/orgs/${orgSlug}/boms/${bomId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: bomsKey(orgSlug) }),
  });
}

export function useFreezeRevision(orgSlug: string | undefined, bomId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revisionId: number) =>
      api.postJson<BomRevisionSummary>(
        `/api/orgs/${orgSlug}/boms/${bomId}/revisions/${revisionId}/freeze`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: revisionsKey(orgSlug, bomId) });
      qc.invalidateQueries({ queryKey: bomKey(orgSlug, bomId) });
      qc.invalidateQueries({ queryKey: bomsKey(orgSlug) });
    },
  });
}

export function useNewRevision(orgSlug: string | undefined, bomId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.postJson<BomRevisionSummary>(`/api/orgs/${orgSlug}/boms/${bomId}/revisions`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: revisionsKey(orgSlug, bomId) });
      qc.invalidateQueries({ queryKey: bomKey(orgSlug, bomId) });
      qc.invalidateQueries({ queryKey: bomsKey(orgSlug) });
    },
  });
}

export function useAddLine(orgSlug: string | undefined, bomId: number, revisionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      part_id: number;
      quantity: number;
      reference_designators?: string[];
      notes?: string | null;
    }) =>
      api.postJson<BomLine>(
        `/api/orgs/${orgSlug}/boms/${bomId}/revisions/${revisionId}/lines`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: revisionKey(orgSlug, bomId, revisionId) });
      qc.invalidateQueries({ queryKey: revisionsKey(orgSlug, bomId) });
    },
  });
}

export function useUpdateLine(
  orgSlug: string | undefined,
  bomId: number,
  revisionId: number,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      lineId: number;
      // Swapping the line to point at a different part — same backend
      // endpoint, the server just validates that the new id belongs to
      // the same org and the revision is still a draft.
      part_id?: number;
      quantity?: number;
      reference_designators?: string[];
      notes?: string | null;
    }) => {
      const { lineId, ...patch } = input;
      return api.patchJson<BomLine>(
        `/api/orgs/${orgSlug}/boms/${bomId}/revisions/${revisionId}/lines/${lineId}`,
        patch,
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: revisionKey(orgSlug, bomId, revisionId) }),
  });
}

export function useDeleteLine(
  orgSlug: string | undefined,
  bomId: number,
  revisionId: number,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: number) =>
      api.delete<unknown>(
        `/api/orgs/${orgSlug}/boms/${bomId}/revisions/${revisionId}/lines/${lineId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: revisionKey(orgSlug, bomId, revisionId) });
      qc.invalidateQueries({ queryKey: revisionsKey(orgSlug, bomId) });
    },
  });
}

export function useAvailability(
  orgSlug: string | undefined,
  bomId: number | null,
  revisionId: number | null,
  buildQty: number,
  enabled: boolean,
) {
  return useQuery<AvailabilityResult>({
    queryKey: ['availability', orgSlug, bomId, revisionId, buildQty],
    queryFn: () =>
      api.get<AvailabilityResult>(
        `/api/orgs/${orgSlug}/boms/${bomId}/revisions/${revisionId}/availability?build_qty=${buildQty}`,
      ),
    enabled: enabled && Boolean(orgSlug && bomId && revisionId && buildQty > 0),
  });
}

export function useWhereUsed(orgSlug: string | undefined, partId: number | null) {
  return useQuery<WhereUsedRow[]>({
    queryKey: ['where-used', orgSlug, partId],
    queryFn: () =>
      api.get<WhereUsedRow[]>(`/api/orgs/${orgSlug}/parts/${partId}/where-used`),
    enabled: Boolean(orgSlug && partId),
  });
}
